import { supabase } from '@/integrations/supabase/client';
import { setPendingCheckout } from '@/utils/pendingCheckout';
import { getCheckoutPricing } from '@/data/countryPricing';
import { langPrefix, type Lang } from '@/i18n';
import { trackEvent } from '@/lib/analytics';

export function paymentSuccessUrl(lang: string): string {
  const prefix = langPrefix((lang as Lang) || 'ko');
  return `${window.location.origin}${prefix}/payment-success`;
}

export function checkoutFailMessage(lang: string): string {
  const map: Record<string, string> = {
    ko: '결제 페이지를 열지 못했습니다. 다시 시도해 주세요.',
    ja: '決済ページを開けませんでした。もう一度お試しください。',
    en: 'Could not open checkout. Please try again.',
    vi: 'Không mở được trang thanh toán. Hãy thử lại.',
    th: 'เปิดหน้าชำระเงินไม่ได้ กรุณาลองอีกครั้ง',
    id: 'Gagal membuka halaman bayar. Coba lagi.',
    'zh-TW': '無法開啟結帳頁，請再試一次。',
    'zh-CN': '无法打开结账页，请再试一次。',
    ms: 'Tidak dapat buka halaman bayaran. Sila cuba lagi.',
    'pt-BR': 'Não foi possível abrir o pagamento. Tente de novo.',
    my: 'ငွေပေးချေမှု စာမျက်နှာ ဖွင့်မရပါ။ ထပ်ကြိုးစားပါ။',
    de: 'Checkout konnte nicht geöffnet werden. Bitte erneut versuchen.',
    es: 'No se pudo abrir el pago. Inténtalo de nuevo.',
    fr: 'Impossible d’ouvrir le paiement. Réessayez.',
    ar: 'تعذر فتح صفحة الدفع. حاول مرة أخرى.',
  };
  return map[lang] || map.en;
}

export async function startPaidCheckout(opts: {
  planId: string;
  billing: 'monthly' | 'yearly';
  provider: 'dodo' | 'paypal';
  lang: string;
  resumeAfterAuth?: boolean;
}): Promise<{ ok: boolean; message?: string }> {
  setPendingCheckout({
    planId: opts.planId,
    billing: opts.billing,
    provider: opts.provider,
    lang: opts.lang,
    resumeAfterAuth: !!opts.resumeAfterAuth,
  });

  trackEvent('checkout_start', {
    plan: opts.planId,
    billing: opts.billing,
    provider: opts.provider,
    lang: opts.lang,
  });

  const pricing = getCheckoutPricing(opts.lang);
  const amount = String(opts.billing === 'yearly' ? pricing.yearly : pricing.monthly);

  const startPayPal = async () => {
    const { data, error } = await supabase.functions.invoke('create-paypal-order', {
      body: {
        planId: opts.planId,
        billing: opts.billing,
        currency: pricing.currency,
        amount,
      },
    });
    if (error || !data?.approvalUrl) {
      throw new Error(data?.error || error?.message || 'PayPal order creation failed');
    }
    window.location.href = data.approvalUrl;
  };

  try {
    if (opts.provider === 'paypal') {
      await startPayPal();
      trackEvent('checkout_opened', { lang: opts.lang, planId: opts.planId, billingCycle: opts.billing, provider: 'paypal' });
      return { ok: true };
    }

    const { data, error } = await supabase.functions.invoke('create-dodo-checkout', {
      body: {
        planId: opts.planId,
        billing: opts.billing,
        returnUrl: paymentSuccessUrl(opts.lang),
        country: pricing.country,
        lang: opts.lang,
      },
    });

    if (data?.error === 'unsupported_country') {
      trackEvent('checkout_fallback_paypal', {
        lang: opts.lang,
        planId: opts.planId,
        billingCycle: opts.billing,
        reason: `unsupported_country:${data?.country || ''}`,
      });
      await startPayPal();
      trackEvent('checkout_opened', { lang: opts.lang, planId: opts.planId, billingCycle: opts.billing, provider: 'paypal' });
      return { ok: true };
    }

    if (error || !data?.checkoutUrl) {
      throw new Error(data?.error || error?.message || 'Checkout creation failed');
    }
    window.location.href = data.checkoutUrl;
    trackEvent('checkout_opened', { lang: opts.lang, planId: opts.planId, billingCycle: opts.billing, provider: 'dodo' });
    return { ok: true };
  } catch (err) {
    trackEvent('checkout_error', {
      lang: opts.lang,
      planId: opts.planId,
      billingCycle: opts.billing,
      provider: opts.provider,
      message: err instanceof Error ? err.message : 'unknown',
    });
    return { ok: false, message: checkoutFailMessage(opts.lang) };
  }
}
